//@flow

import { injectIntl } from 'react-intl';

import { handleShare } from '../../helpers/handleShare';

const React = require('react');
const ShareIcon = require('dibs-icons/exports/Share').default;
const EnvelopeIcon = require('dibs-icons/exports/Envelope').default;
const FacebookIcon = require('dibs-icons/exports/Facebook').default;
const PinterestIcon = require('dibs-icons/exports/Pinterest').default;
const TwitterIcon = require('dibs-icons/exports/Twitter').default;

const styles = require('./SocialShare-style.scss');
const classnames = require('classnames');

const ENVELOPE: 'envelope' = 'envelope';
const FACEBOOK: 'facebook' = 'facebook';
const PINTEREST: 'pinterest' = 'pinterest';
const TWITTER: 'twitter' = 'twitter';
const THANK_YOU_MESSAGE = 'THANKS!';

const DEFAULT_ICONS = {
    [ENVELOPE]: EnvelopeIcon,
    [FACEBOOK]: FacebookIcon,
    [PINTEREST]: PinterestIcon,
    [TWITTER]: TwitterIcon,
};

const DEFAULT_ICON_ORDER = [ENVELOPE, FACEBOOK, PINTEREST, TWITTER];

type SocialIconsType = { [key: string]: string };
type IconsList = $Keys<typeof DEFAULT_ICONS>;
type onClickSocialIcon = (event: SyntheticEvent<HTMLElement>) => void;

type Props = {
    disable?: boolean,
    socialIcons: SocialIconsType,
    iconsOrder: Array<IconsList>,
    thankYouMsg: string,
    firmName: string,
    photoPath: string,
    urlLabel: string,
    intl: intlShape,
};

type State = {
    isOpen: boolean,
    showThanks: boolean,
};

type SocialIconsProps = {
    socialIcons: SocialIconsType,
    iconsOrder: Array<IconsList>,
    onClick: onClickSocialIcon,
    thankYouMsg: string,
    showThankYouMsg: boolean,
    firmName: string,
    photoPath: string,
    urlLabel: string,
    intl: intlShape,
};

const SocialIcons = ({
    socialIcons,
    iconsOrder,
    onClick,
    thankYouMsg,
    showThankYouMsg,
    firmName,
    photoPath,
    urlLabel,
    intl,
}: SocialIconsProps): React.Node => {
    const className = classnames(styles.socialWrapper, 'rowFlex');
    const socialButtonClass = classnames(styles.button, styles.social, 'colXs3');
    const thankYouMessageClass = classnames(styles.thankYouMessage, 'colXs12');

    return (
        <div className={className}>
            {iconsOrder.map(icon => {
                const Icon = socialIcons[icon];
                if (!Icon) {
                    return null;
                }

                return (
                    <a
                        key={icon}
                        className={socialButtonClass}
                        onClick={e => {
                            e.preventDefault();
                            handleShare(icon, { firmName, photoPath, urlLabel }, intl);
                            onClick(e);
                        }}
                    >
                        <Icon className={styles.socialSvg} />
                    </a>
                );
            })}
            {showThankYouMsg ? <div className={thankYouMessageClass}>{thankYouMsg}</div> : null}
        </div>
    );
};

/**
 * @class SocialShare
 * Has 3 states
 *  - Closed
 *  - Open with icons showing
 *  - Open with Thank You message showing
 */
class SocialShareComponent extends React.Component<Props, State> {
    static defaultProps = {
        socialIcons: DEFAULT_ICONS,
        iconsOrder: DEFAULT_ICON_ORDER,
        thankYouMsg: THANK_YOU_MESSAGE,
    };
    constructor(props: Props) {
        super(props);

        this._onClickShare = this._onClickShare.bind(this);
        this._onClickSocialIcon = this._onClickSocialIcon.bind(this);
        this.closeComponent = this.closeComponent.bind(this);

        this.state = {
            isOpen: false, // is the component open or closed
            showThanks: false, // show the thank you message
        };
    }

    componentDidMount() {
        window.addEventListener('scroll', this.closeComponent);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.closeComponent);
    }

    /**
     * Closes the component if it is open
     */
    closeComponent = () => {
        if (this.state.isOpen) {
            this.setState({
                isOpen: false,
                showThanks: false,
            });
        }
    };

    /**
     * Opens the component if it is closed
     */
    openComponent = () => {
        if (!this.state.isOpen) {
            this.setState({
                isOpen: true,
                showThanks: false,
            });
        }
    };

    /**
     * Shows the thank you message and then closes the component
     */
    showThanks = () => {
        if (this.state.isOpen && !this.state.showThanks) {
            this.setState({ showThanks: true });
        }
    };

    /**
     * Convenience method to show Thank You Message for 1 second and then close the component
     */
    showThanksAndClose = () => {
        this.showThanks();
        setTimeout(() => this.closeComponent(), 1000);
    };

    /**
     * Handler for when share icon is clicked
     *  - sets the state to open with icons showing
     *  - sets the state to closed
     */
    _onClickShare = (e: SyntheticEvent<HTMLElement>) => {
        e.preventDefault();
        if (!this.props.disable) {
            if (this.state.isOpen) {
                this.closeComponent();
            } else {
                this.openComponent();
            }
        }
    };

    /**
     * Handler for when social icon is clicked
     */
    _onClickSocialIcon = (event: SyntheticEvent<HTMLElement>) => {
        event.preventDefault();
        try {
            this.showThanksAndClose();
        } catch (error) {
            this.closeComponent();
        }
    };

    render() {
        const className = classnames(styles.container, {
            [styles.isOpen]: this.state.isOpen,
            [styles.showThanks]: this.state.showThanks,
        });

        const { socialIcons, iconsOrder, firmName, photoPath, urlLabel, intl } = this.props;

        return (
            <div className={className}>
                <div className={styles.wrapper}>
                    {/* Social Icons */}
                    <SocialIcons
                        intl={intl}
                        firmName={firmName}
                        photoPath={photoPath}
                        urlLabel={urlLabel}
                        socialIcons={socialIcons}
                        iconsOrder={iconsOrder}
                        onClick={this._onClickSocialIcon}
                        thankYouMsg={this.props.thankYouMsg}
                        showThankYouMsg={this.state.showThanks}
                    />

                    {/* Share Icon */}
                    <a
                        ref="share"
                        className={`${styles.button} ${styles.share}`}
                        onClick={this._onClickShare}
                        href="#"
                    >
                        <ShareIcon />
                    </a>
                </div>
            </div>
        );
    }
}
export const SocialShare = injectIntl(SocialShareComponent);
